<script setup lang="ts">
/**
 * PUT · DELETE /data-objects/{id}/knowledge-bases/{knowledgeBaseId}: which memory collections this
 * document is projected into. Toggling a collection adds or removes the projection; a new collection is
 * created first (POST /knowledge-bases) and then linked. The server projects the active interpretation
 * and never re-reads the source; it refuses a collection that already holds another document.
 */
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import StatusCell from '~/components/ui/StatusCell.vue'

const props = defineProps<{ entity: string; collections: { id: string; name: string; facts: string; status: string }[]; linked: string[] }>()
const emit = defineEmits<{ close: []; submit: [payload: { add: string[]; remove: string[]; newName: string }] }>()
const chosen = ref<string[]>([...props.linked])
const fresh = ref(false)
const newName = ref('')
const toggle = (id: string) => { chosen.value = chosen.value.includes(id) ? chosen.value.filter((entry) => entry !== id) : [...chosen.value, id] }
const add = computed(() => chosen.value.filter((id) => !props.linked.includes(id)))
const remove = computed(() => props.linked.filter((id) => !chosen.value.includes(id)))
const creating = computed(() => fresh.value && newName.value.trim().length > 0)
const changed = computed(() => add.value.length > 0 || remove.value.length > 0 || creating.value)
const line = computed(() => { const n = chosen.value.length + (creating.value ? 1 : 0); return n === 0 ? 'No collection consults this document' : `${n} ${n === 1 ? 'collection consults' : 'collections consult'} this document` })
</script>

<template>
  <Dialog title="Add to a collection" :meta="entity" :width="480" @close="emit('close')">
    <template #icon><Icon name="memory" /></template>
    <p v-if="!collections.length" class="m-0 text-xs text-muted-foreground">This project has no memory collections yet. Create one below; it is linked as soon as it exists.</p>
    <PickList v-else v-slot="{ shown }" :items="collections" unit="collections" :chosen="chosen.length">
      <Choice v-for="collection in shown" :key="collection.id" multiple :title="collection.name" :facts="collection.facts" :selected="chosen.includes(collection.id)" @click="toggle(collection.id)">
        <span class="ml-auto shrink-0"><StatusCell :status="collection.status" /></span>
      </Choice>
    </PickList>
    <Choice multiple title="New collection" facts="created with the default settings, then linked" :selected="fresh" @click="fresh = !fresh" />
    <Field v-if="fresh" v-model="newName" label="Name" placeholder="support-tickets" required />
    <p class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">{{ line }} · a collection holds one document's projection</p>
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Save" variant="primary" :disabled="!changed" @click="emit('submit', { add, remove, newName: creating ? newName.trim() : '' })"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
